import {
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
  getPageNumber,
  getQueries,
  mapTargetBySelectors,
} from '@/features/shared/HOCs/withGTM';
import { OnDatalayerProps } from '@/features/shared/HOCs/withGTM.interface';
import qs from 'query-string';

enum Selectors {
  // Target
  Category = '.gtm-category',
  Sort = '.MuiTableSortLabel-root',
  Pagination = '.MuiTablePagination-actions > a',
  Download = '.gtm-download',
  Print = '.gtm-print',
  // Containers
  PaginationContainer = '.MuiTablePagination-actions',
}

export const mapTarget = mapTargetBySelectors([
  Selectors.Category,
  Selectors.Sort,
  Selectors.Pagination,
  Selectors.Download,
  Selectors.Print,
]);

export const onDatalayer = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const isCategory = target?.classList.contains(Selectors.Category.replace('.', ''));
  const isSort = target?.classList.contains(Selectors.Sort.replace('.', ''));
  const isPagination = !!target?.closest(Selectors.PaginationContainer);
  const isDownload = target?.classList.contains(Selectors.Download.replace('.', ''));
  const isPrint = target?.classList.contains(Selectors.Print.replace('.', ''));
  const variantName = (target?.closest('[data-datalayer-variant]') as HTMLDivElement)?.dataset
    ?.datalayerVariant;

  const {
    q: searchQuery = '',
    page = '1',
    sortBy: sortOption = 'manufacturer',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortType = 'asc',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    size = 10,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    category_type: categoryType = 'camera',
    ...filters
  } = qs.parse(window.location.search);

  const searchFilters = getQueries(filters);

  if (isCategory) {
    // After clicking on category "searchFilters" are reseted.
    return {
      event: 'clickCategory',
      eventCategory: 'SDL Search',
      searchFilters: '',
      searchQuery,
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isSort) {
    return {
      event: 'sortResults',
      eventCategory: 'SDL Search',
      sortOption,
      searchFilters,
      searchQuery,
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isPagination) {
    const pageNumber = getPageNumber({ target, page: Number(page) });

    return {
      event: 'clickPagination',
      eventCategory: 'SDL Search',
      pageNumber,
      searchFilters,
      searchQuery,
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isDownload) {
    return {
      event: 'clickDownload',
      eventCategory: 'SDL Search',
      pageNumber: page,
      searchFilters,
      searchQuery,
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isPrint) {
    return {
      event: 'clickPrint',
      eventCategory: 'SDL Search',
      pageNumber: page,
      searchFilters,
      searchQuery,
      ...(variantName && { variant_name: variantName }),
    };
  }

  return undefined;
};

export const onDatalayerCustom = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const { params, event, variantName = '', ...args } = target || {};
  const isClickResult = /^clickResult$/.test(event);
  const isPrint = /^printPdf$/.test(event);
  const isSearchQuery = /^searchQuery$/.test(event);
  const isApplyFilters = /^applyFilters$/.test(event);

  const {
    q: searchQuery = '',
    page = '1',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortBy = 'manufacturer',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortType = 'asc',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    size = 10,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    category_type: categoryType = 'camera',
    ...filters
  } = qs.parse(window.location.search);
  const searchFilters = getQueries(filters);

  if (isClickResult) {
    return {
      event,
      eventCategory: 'SDL Search',
      ...args,
      searchQuery,
      searchFilters,
      pageNumber: page,
      href: window.location.href,
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isPrint) {
    return {
      event: 'clickPrint',
      eventCategory: 'SDL Search',
      pageNumber: page,
      searchFilters,
      searchQuery,
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isSearchQuery || isApplyFilters) {
    return {
      ...args,
      event,
      eventCategory: 'SDL Search',
      searchFilters: getQueries(params?.filters),
      searchQuery: params?.q || '',
      ...(variantName && { variant_name: variantName }),
    };
  }

  return undefined;
};

export const onDatalayerV4Custom = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const searchFilters = getDataLayerSearchFilters(target?.filters);
  const numFilters = getDataLayerNumFilters(target?.filters);
  const searchFiltersObject = getDataLayerSearchFiltersObject(target?.filters);
  const variantName = target?.variantName || '';

  return {
    event: 'trackEvent-sdlSearchResult',
    event_name: 'view_sdl_search_results',
    search_term: target?.search_term,
    num_search_result: target?.num_search_result,
    is_revision: target?.is_revision,
    ...(!!searchFilters && { search_filters: searchFilters }),
    num_search_filters: numFilters,
    sdl_category: target?.sdl_category,
    index_num: String(target?.index_num || 0),
    ...searchFiltersObject,
    ...(variantName && { variant_name: variantName }),
  };
};

export const getMakeAndModel = (args: any) => {
  if (args.length > 0) {
    const {
      basic_information: { raw },
    } = args[0] || {};

    const { ManufacturerName, ModelName } = JSON.parse(raw || {});

    return `${ManufacturerName} ${ModelName}`;
  }

  return '';
};
